import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { MarginAnalysisState } from './useMarginAnalysis';
import CustomTooltip from './CustomTooltip';
import { axisLabelStyle } from './utils';
interface ChartSectionProps {
  state: MarginAnalysisState;
}
const ChartSection: React.FC<ChartSectionProps> = ({
  state
}) => {
  const {
    marginChartData,
    costChartData,
    grossProfitMargin
  } = state;

  // Dynamic Y-axis ticks that extend from -100% and beyond based on data
  const getDynamicYAxisTicks = (chartData: any[]) => {
    if (!chartData || chartData.length === 0) {
      return [-100, -80, -60, -40, -20, 0, 20, 40, 60, 80, 100];
    }

    // Find min and max margins in the data
    const margins = chartData.map(item => item.margin);
    const minMargin = Math.min(...margins);
    const maxMargin = Math.max(...margins);

    // Base ticks from -100% to 100%
    const baseTicks = [-100, -80, -60, -40, -20, 0, 20, 40, 60, 80, 100];

    // Minimum margin should not go below -100%
    const effectiveMinMargin = Math.max(minMargin, -100);

    // If maximum margin goes above 100%, add additional positive ticks
    if (maxMargin > 100) {
      const additionalTicks = [];
      let tick = 120;
      while (tick <= Math.ceil(maxMargin / 20) * 20 + 20) {
        additionalTicks.push(tick);
        tick += 20;
      }
      return [...baseTicks, ...additionalTicks];
    }
    return baseTicks;
  };

  // Calculate domain for Y-axis based on data
  const getYAxisDomain = (chartData: any[]) => {
    if (!chartData || chartData.length === 0) {
      return [-100, 100];
    }
    const margins = chartData.map(item => item.margin);
    const minMargin = Math.min(...margins);
    const maxMargin = Math.max(...margins);

    // Ensure we show at least from -100% to 100%, but minimum cannot go below -100%
    const domainMin = Math.max(-100, Math.floor(minMargin / 20) * 20 - 20);
    const domainMax = Math.max(100, Math.ceil(maxMargin / 20) * 20 + 20);
    return [domainMin, domainMax];
  };
  const priceYAxisTicks = getDynamicYAxisTicks(marginChartData);
  const priceYAxisDomain = getYAxisDomain(marginChartData);
  const costYAxisTicks = getDynamicYAxisTicks(costChartData);
  const costYAxisDomain = getYAxisDomain(costChartData);
  return <div className="col-span-3 grid grid-cols-2 gap-3">
      {/* Price Simulation Card - Left Side */}
      <Card className="bg-gradient-to-br from-[#1e293b] to-[#111827] border-primary/20 shadow-md hover:shadow-[#6366F1]/10 transition-all duration-300 rounded-md">
        <CardContent className="p-2 py-[40px]">
          <h3 className="text-[15px] font-semibold mb-2 text-primary-light">Price-Margin Simulation</h3>
          <div className="h-[380px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={marginChartData} margin={{
              top: 15,
              right: 5,
              left: 5,
              bottom: 15
            }}>
                <CartesianGrid vertical={false} stroke="#334155" />
                <XAxis dataKey="change" tick={{
                fill: '#94a3b8',
                fontSize: 10
              }} tickFormatter={tick => `${tick > 0 ? '+' : ''}${tick}%`} axisLine={{
                stroke: '#475569'
              }} label={{
                value: 'Price Change (%)',
                position: 'bottom',
                offset: 5,
                ...axisLabelStyle
              }} />
                <YAxis ticks={priceYAxisTicks} tick={{
                fill: '#94a3b8',
                fontSize: 10
              }} tickFormatter={tick => `${tick.toFixed(1)}%`} axisLine={{
                stroke: '#475569'
              }} domain={priceYAxisDomain} label={{
                value: 'Margin (%)',
                angle: -90,
                position: 'left',
                offset: 0,
                ...axisLabelStyle
              }} />
                <Tooltip position={{
                x: 0,
                y: 0
              }} coordinate={{
                x: 0,
                y: 0
              }} cursor={{
                stroke: '#475569',
                strokeDasharray: '3 3'
              }} content={<CustomTooltip chartType="price" currentMargin={grossProfitMargin} />} />
                <Line type="monotone" dataKey="margin" stroke="#6366F1" strokeWidth={2} dot={false} activeDot={{
                r: 4,
                stroke: '#6366F1',
                strokeWidth: 2,
                fill: '#1E293B'
              }} />
                {/* Reference line for current margin */}
                <Line dataKey={() => grossProfitMargin} stroke="#475569" strokeDasharray="3 3" strokeWidth={1} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Cost Simulation Card - Right Side */}
      <Card className="bg-gradient-to-br from-[#1e293b] to-[#111827] border-primary/20 shadow-md hover:shadow-[#10B981]/10 transition-all duration-300 rounded-md">
        <CardContent className="p-2 py-[40px]">
          <h3 className="text-[15px] font-semibold mb-2 text-primary-light">Cost-Margin Simulation</h3>
          <div className="h-[380px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={costChartData} margin={{
              top: 15,
              right: 5,
              left: 5,
              bottom: 15
            }}>
                <CartesianGrid vertical={false} stroke="#334155" />
                <XAxis dataKey="change" tick={{
                fill: '#94a3b8',
                fontSize: 10
              }} tickFormatter={tick => `${tick > 0 ? '+' : ''}${tick}%`} axisLine={{
                stroke: '#475569'
              }} label={{
                value: 'Cost Change (%)',
                position: 'bottom',
                offset: 5,
                ...axisLabelStyle
              }} />
                <YAxis ticks={costYAxisTicks} tick={{
                fill: '#94a3b8',
                fontSize: 10
              }} tickFormatter={tick => `${tick.toFixed(1)}%`} axisLine={{
                stroke: '#475569'
              }} domain={costYAxisDomain} label={{
                value: 'Margin (%)',
                angle: -90,
                position: 'left',
                offset: 0,
                ...axisLabelStyle
              }} />
                <Tooltip position={{
                x: 0,
                y: 0
              }} coordinate={{
                x: 0,
                y: 0
              }} cursor={{
                stroke: '#475569',
                strokeDasharray: '3 3'
              }} content={<CustomTooltip chartType="cost" currentMargin={grossProfitMargin} />} />
                <Line type="monotone" dataKey="margin" stroke="#10B981" strokeWidth={2} dot={false} activeDot={{
                r: 4,
                stroke: '#10B981',
                strokeWidth: 2,
                fill: '#1E293B'
              }} />
                {/* Reference line for current margin */}
                <Line dataKey={() => grossProfitMargin} stroke="#475569" strokeDasharray="3 3" strokeWidth={1} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>;
};
export default ChartSection;